import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Leaf, 
  AlertCircle,
  CreditCard,
  Clock,
  Calendar,
  DollarSign,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { formatPacificDate } from '../utils/dateTime';
import { formatCurrency } from '../utils/formatters';
import { LogOut } from 'lucide-react';
import { PatientDetailsModal } from '../components/PatientDetailsModal';

interface StaleMembership {
  id: string;
  membership_name: string;
  membership_patient_id: string;
  patient_first_name: string;
  patient_last_name: string;
  start_date: string | null;
  last_run: string | null;
  days_since_last_run: number;
  charge_amount: number;
  internal_interval: string;
  patient_id: string;
}

export function MembershipIssues() {
  const navigate = useNavigate();
  const { user, userRoles } = useAuth();
  const isAdmin = userRoles.includes('admin');
  const [staleMemberships, setStaleMemberships] = useState<StaleMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lookbackDays, setLookbackDays] = useState(30);
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<keyof StaleMembership | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  

  const [minDaysOverdue, setMinDaysOverdue] = useState(1);


//  useEffect(() => {
//    if (!isAdmin) {
//      navigate('/settings');
//      return;
//    }
//    fetchStaleMemberships();
//  }, [isAdmin, navigate, lookbackDays]);

  useEffect(() => {
  fetchStaleMemberships();
}, [lookbackDays]);

  const fetchStaleMemberships = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase.rpc(
        'get_stale_active_memberships',
        { p_lookback_days: lookbackDays }
      );

      if (error) throw error;
      setStaleMemberships(data || []);
    } catch (error: any) {
      console.error('Error fetching stale memberships:', error);
      setError('Failed to load stale memberships. Please try again.');
    } finally {
      setLoading(false);
    }
  };

const filteredMemberships = staleMemberships.filter(
  (m) => m.days_since_last_run >= minDaysOverdue
);

  const totalAmount = filteredMemberships.reduce((sum, m) => sum + (m.charge_amount || 0), 0);
  
 const sortedMemberships = [...filteredMemberships].sort((a, b) => {
    if (!sortKey) return 0;
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortAsc ? aVal - bVal : bVal - aVal;
    }
    return sortAsc
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const handlePatientClick = (patientId: string) => {
    setSelectedPatientId(patientId);
    setShowPatientDetails(true);
  };

  const handleClosePatientDetails = () => {
    setShowPatientDetails(false);
    setSelectedPatientId(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-green-50 to-blue-50">
      {/* Fixed Header */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Leaf className="h-6 w-6 text-green-600" />
              <div className="ml-2">
                <span className="text-xl font-semibold text-gray-900">THRIVE</span>
                <span className="ml-2 text-sm text-gray-500">Membership Issues</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">{user?.email}</span>
              <div className="flex flex-wrap gap-2">
                {userRoles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    {role}
                  </span>
                ))}
              </div>
              <button
                onClick={() => navigate('/settings')}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Fixed Search Bar */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="mr-4 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h2 className="text-xl font-semibold text-gray-900">Stale Memberships</h2>
              {!loading && staleMemberships.length > 0 && (
                <span className="ml-4 text-sm text-gray-500">
  {staleMemberships.length} membership{staleMemberships.length > 1 ? 's' : ''} found
  {' '}
  ({formatCurrency(totalAmount)})
</span>

              )}
            </div>
            <div className="flex items-center space-x-4">
  <div className="flex items-center">
    <span className="text-sm text-gray-700 mr-2">Lookback period:</span>
    <select
      value={lookbackDays}
      onChange={(e) => setLookbackDays(parseInt(e.target.value))}
      className="rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
    >
      <option value={15}>15 days</option>
      <option value={30}>30 days</option>
      <option value={45}>45 days</option>
      <option value={60}>60 days</option>
      <option value={90}>90 days</option>
    </select>
  </div>
  <div className="flex items-center">
    <label htmlFor="minDaysOverdue" className="text-sm text-gray-700 mr-2">Min Days Overdue:</label>
    <input
      id="minDaysOverdue"
      type="number"
      min={0}
      value={minDaysOverdue}
      onChange={(e) => setMinDaysOverdue(parseInt(e.target.value))}
      className="w-20 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
    />
  </div>
</div>

          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full py-6">
          <div className="bg-white shadow-lg rounded-lg flex flex-col">
            {error && (
              <div className="p-4 bg-red-50 border-b border-red-200">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
  <table className="min-w-full table-fixed divide-y divide-gray-200 text-xs">
    <thead className="bg-gray-50">
      <tr>
        <th
          className="w-48 px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => {
            setSortKey('patient_last_name');
            setSortAsc(sortKey === 'patient_last_name' ? !sortAsc : true);
          }}
        >
          Patient
        </th>
        <th
          className="w-40 px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => {
            setSortKey('membership_name');
            setSortAsc(sortKey === 'membership_name' ? !sortAsc : true);
          }}
        >
          Membership
        </th>
        <th className="w-32 px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
        <th className="w-32 px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Last Run</th>
        <th
          className="w-36 px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => {
            setSortKey('days_since_last_run');
            setSortAsc(sortKey === 'days_since_last_run' ? !sortAsc : true);
          }}
        >
          Days Overdue
        </th>
        <th
          className="w-28 px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
          onClick={() => {
            setSortKey('charge_amount');
            setSortAsc(sortKey === 'charge_amount' ? !sortAsc : true);
          }}
        >
          Amount
        </th>
        <th className="w-24 px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Interval</th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-200">
      {loading ? (
        <tr>
          <td colSpan={7} className="px-6 py-4 text-center">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
            </div>
          </td>
        </tr>
      ) : sortedMemberships.length === 0 ? (
        <tr>
          <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
            <div className="flex flex-col items-center py-6">
              <AlertCircle className="h-12 w-12 text-gray-400 mb-3" />
              <p className="text-lg font-medium">No stale memberships found</p>
              <p className="text-sm mt-1">All active memberships have been processed within the selected range</p>
            </div>
          </td>
        </tr>
      ) : (
        sortedMemberships.map((membership) => (
          <tr
            key={membership.id}
            className="hover:bg-gray-50 cursor-pointer"
            onClick={() => handlePatientClick(membership.patient_id)}
          >
            <td className="w-48 px-4 py-2 truncate text-gray-900 font-medium">
              {membership.patient_last_name}, {membership.patient_first_name}
            </td>
            <td className="w-40 px-4 py-2 truncate text-gray-700">{membership.membership_name}</td>
            <td className="w-32 px-4 py-2 text-gray-500">
              {membership.start_date ? formatPacificDate(membership.start_date) : 'N/A'}
            </td>
            <td className="w-32 px-4 py-2 text-gray-500">
              {membership.last_run ? formatPacificDate(membership.last_run) : 'Never'}
            </td>
            <td className="w-36 px-4 py-2">
              <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full font-medium ${
                membership.days_since_last_run > 60 
                  ? 'bg-red-100 text-red-800' 
                  : membership.days_since_last_run > 45
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                <AlertTriangle className="h-3 w-3 mr-1" />
                {membership.days_since_last_run} days
              </div>
            </td>
            <td className="w-28 px-4 py-2 text-gray-900 font-medium">
              <div className="flex items-center">
                <DollarSign className="h-3 w-3 text-gray-400 mr-1" />
                {formatCurrency(membership.charge_amount)}
              </div>
            </td>
            <td className="w-24 px-4 py-2 text-gray-500">{membership.internal_interval}</td>
          </tr>
        ))
      )}
    </tbody>
  </table>
</div>

          </div>
        </div>
      </div>

      {showPatientDetails && (
        <PatientDetailsModal
          patientId={selectedPatientId}
          isOpen={showPatientDetails}
          onClose={handleClosePatientDetails}
        />
      )}
    </div>
  );
}
